export interface BankAccount {
    accountNumber: string; // Р/сч
    bankName: string; // Название банка
    correspondentAccount?: string; // К/сч
    bik: string; // БИК
}

export interface AdditionalLocation {
    name: string; // Название базы/офиса
    address: string; // Адрес
    workingHours: string; // Часы работы
}

export interface DeclensionForms {
    nominative: string;
    genitive: string;
    dative: string;
    accusative: string;
    instrumental: string;
    prepositional: string;
}

export interface RegionForms {
    nominative: string;
    genitive: string;
    dative?: string;
}

export interface BranchPlaceholders {
    city: DeclensionForms;
    region: RegionForms;
    country: string;
    /** Куда возим в первую очередь — это же значение уходит в #direction# */
    direction: string;
}

/** Территория обслуживания для Schema.org areaServed */
export interface ServedArea {
    '@type': 'City' | 'AdministrativeArea';
    name: string;
}

export interface OpeningHours {
    dayOfWeek: string[];
    opens: string;
    closes: string;
}

/**
 * Данные филиала для структурированной разметки. Отделены от человекочитаемых
 * `address` / `workingHours`: те — проза для вёрстки, эти — поля Schema.org.
 */
export interface BranchSeo {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    /** Опционально: лучше не отдать индекс, чем отдать неверный */
    postalCode?: string;
    /** Опционально: неверные координаты для LocalBusiness хуже их отсутствия */
    geo?: { latitude: number; longitude: number };
    openingHours: OpeningHours;
    areaServed: ServedArea[];
}

export interface BranchData {
    code: string;
    name: string;
    phone: string;
    email: string;
    address: string;
    workingHours: {
        weekdays: string; // Пн-Чт
        friday: string; // Пт
        weekend: string; // Сб-Вс
        lunch?: string; // Обед
    };
    shippingHours: string; // отгрузки с...
    url: string; // URL для перехода на филиал (внутренний путь)
    telegram?: string; // Telegram ссылка
    wa?: string; // Telegram ссылка
    mapUrl?: string; // URL для карты Яндекс
    additionalLocations?: AdditionalLocation[]; // Дополнительные базы/офисы
    requisites?: {
        companyName: string; // Наименование
        legalAddress: string; // Юридический адрес
        actualAddress?: string; // Фактический адрес
        inn: string; // ИНН
        kpp?: string; // КПП
        ogrn: string; // ОГРН
        okpo?: string; // ОКПО
        okato?: string; // ОКАТО
        bankAccounts?: BankAccount[]; // Расчетные счета
        director?: string; // Генеральный директор
        basis?: string; // Основание (на основании чего действует)
    };
    placeholders?: BranchPlaceholders;
    seo?: BranchSeo;
}

const branches: Record<string, BranchData> = {
    msk: {
        code: 'msk',
        name: 'Москва',
        phone: '+7 (921) 759-07-52',
        email: 'sales@ur-met.ru',
        address: 'г. Москва, Новопесчаная улица, д. 17, корп.3',
        workingHours: {
            weekdays: 'Понедельник — Пятница: с 9:00 до 18:00',
            friday: '',
            weekend: '',
            lunch: '',
        },
        shippingHours: 'отгрузки с 10:00',
        url: '/geo/msk',
        telegram: 'https://t.me/state_street_corporation',
        wa: 'https://wa.me/79217590752?text=%D0%94%D0%BE%D0%B1%D1%80%D1%8B%D0%B9%20%D0%B4%D0%B5%D0%BD%D1%8C!%20',
        mapUrl: 'https://yandex.ru/map-widget/v1/?um=constructor%3Ac53838c573a9870ca086f0c54021f94372db6267ce71e011ec83a0aafd4477b7&amp;source=constructor',
        requisites: {
            companyName: 'ООО «УралМеталл»',
            legalAddress: '195196, г. Санкт-Петербург, ВН.ТЕР.Г. МУНИЦИПАЛЬНЫЙ ОКРУГ МАЛАЯ ОХТА, УЛ РИЖСКАЯ, Д. 5, К. 1, ЛИТЕРА А, ПОМ. 5Н, КОМ.19А, ОФ 408',
            actualAddress: '195196, г. Санкт-Петербург, ВН.ТЕР.Г. МУНИЦИПАЛЬНЫЙ ОКРУГ МАЛАЯ ОХТА, УЛ РИЖСКАЯ, Д. 5, К. 1, ЛИТЕРА А, ПОМ. 5Н, КОМ.19А, ОФ 408',
            inn: '7814608295',
            kpp: '780601001',
            ogrn: '1147847128945',
            okpo: '39443469',
            okato: '40270563000',
            bankAccounts: [
                {
                    accountNumber: '40702810938000050831',
                    bankName: 'ПАО СБЕРБАНК Г. МОСКВА',
                    correspondentAccount: '30101810400000000225',
                    bik: '044525225',
                },
                {
                    accountNumber: '40702810500000487480',
                    bankName: 'ООО "ОЗОН Банк"',
                    correspondentAccount: '30101810645374525068',
                    bik: '044525068',
                },
            ],
            director: 'Понамарев C.Ю.',
            basis: 'действует на основании Устава',
        },
        placeholders: {
            city: {
                nominative: 'Москва',
                genitive: 'Москвы',
                dative: 'Москве',
                accusative: 'Москву',
                instrumental: 'Москвой',
                prepositional: 'в Москве',
            },
            region: {
                nominative: 'Московская область',
                genitive: 'Московской области',
                dative: 'Московской области',
            },
            country: 'Россия',
            direction: 'по Москве и Московской области',
        },
        seo: {
            streetAddress: 'Новопесчаная улица, д. 17, корп. 3',
            addressLocality: 'Москва',
            addressRegion: 'Москва',
            // postalCode и geo намеренно не заполнены: точные значения берём из
            // подтверждённой карточки Яндекс.Бизнеса. Неверные координаты для
            // LocalBusiness вреднее, чем их отсутствие.
            openingHours: {
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                opens: '09:00',
                closes: '18:00',
            },
            areaServed: [
                { '@type': 'City', name: 'Москва' },
                { '@type': 'AdministrativeArea', name: 'Московская область' },
            ],
        },
    },
    spb: {
        code: 'spb',
        name: 'Санкт-Петербург',
        phone: '+7 (921) 759-07-52',
        email: 'sales@ur-met.ru',
        address: 'г. Санкт-Петербург Рижская улица, 5 к.1',
        workingHours: {
            weekdays: 'Понедельник — Пятница: с 9:00 до 18:00',
            friday: '',
            weekend: '',
            lunch: '',
        },
        shippingHours: 'отгрузки с 10:00',
        url: '/', // Главная страница для Санкт-Петербурга (регион по умолчанию)
        telegram: 'https://t.me/state_street_corporation',
      wa: 'https://wa.me/79217590752?text=%D0%94%D0%BE%D0%B1%D1%80%D1%8B%D0%B9%20%D0%B4%D0%B5%D0%BD%D1%8C!%20',
        mapUrl: 'https://yandex.ru/map-widget/v1/?um=constructor%3A27891cdf24d46a0fa6830f1d4229f63eaee9afb7ddd8cb08d7a5e39feeb31d96&amp;source=constructor',
        additionalLocations: [
            {
                name: 'Складская база ЗМК Континенталь',
                address: 'г. Санкт-Петербург, 6-й Предпортовый проезд, дом 12',
                workingHours: 'Понедельник — Пятница: с 9:00 до 18:00',
            },
            {
                name: 'Производственная база ЗМК Континенталь',
                address: 'г. Петергоф, тер. Марьино, дом 18А',
                workingHours: 'Понедельник — Воскресенье: с 9:00 до 20:00',
            },
        ],
        requisites: {
            companyName: 'ООО «УралМеталл»',
            legalAddress: '195196, г. Санкт-Петербург, ВН.ТЕР.Г. МУНИЦИПАЛЬНЫЙ ОКРУГ МАЛАЯ ОХТА, УЛ РИЖСКАЯ, Д. 5, К. 1, ЛИТЕРА А, ПОМ. 5Н, КОМ.19А, ОФ 408',
            actualAddress: '195196, г. Санкт-Петербург, ВН.ТЕР.Г. МУНИЦИПАЛЬНЫЙ ОКРУГ МАЛАЯ ОХТА, УЛ РИЖСКАЯ, Д. 5, К. 1, ЛИТЕРА А, ПОМ. 5Н, КОМ.19А, ОФ 408',
            inn: '7814608295',
            kpp: '780601001',
            ogrn: '1147847128945',
            okpo: '39443469',
            okato: '40270563000',
            bankAccounts: [
                {
                    accountNumber: '40702810938000050831',
                    bankName: 'ПАО СБЕРБАНК Г. МОСКВА',
                    correspondentAccount: '30101810400000000225',
                    bik: '044525225',
                },
                {
                    accountNumber: '40702810500000487480',
                    bankName: 'ООО "ОЗОН Банк"',
                    correspondentAccount: '30101810645374525068',
                    bik: '044525068',
                },
            ],
            director: 'Понамарев C.Ю.',
            basis: 'действует на основании Устава',
        },
        placeholders: {
            city: {
                nominative: 'Санкт-Петербург',
                genitive: 'Санкт-Петербурга',
                dative: 'Санкт-Петербургу',
                accusative: 'Санкт-Петербург',
                instrumental: 'Санкт-Петербургом',
                prepositional: 'в Санкт-Петербурге',
            },
            region: {
                nominative: 'Ленинградская область',
                genitive: 'Ленинградской области',
                dative: 'Ленинградской области',
            },
            country: 'Россия',
            direction: 'по Санкт-Петербургу и Ленинградской области',
        },
        seo: {
            streetAddress: 'Рижская улица, 5 к.1',
            addressLocality: 'Санкт-Петербург',
            addressRegion: 'Санкт-Петербург',
            postalCode: '195196',
            geo: { latitude: 59.9343, longitude: 30.3351 },
            openingHours: {
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                opens: '09:00',
                closes: '18:00',
            },
            areaServed: [
                { '@type': 'City', name: 'Санкт-Петербург' },
                { '@type': 'AdministrativeArea', name: 'Ленинградская область' },
                { '@type': 'AdministrativeArea', name: 'Северо-Западный федеральный округ' },
            ],
        },
    },
};

/**
 * Получить данные филиала по коду
 */
export function getBranchData(code: string): BranchData | null {
    return branches[code] || null;
}

/**
 * Получить все филиалы
 */
export function getAllBranches(): BranchData[] {
    return Object.values(branches);
}

/**
 * Получить текущий филиал (по умолчанию Санкт-Петербург)
 */
export function getCurrentBranch(): BranchData {
    return branches.spb;
}

/**
 * Получить другие филиалы (исключая текущий)
 */
export function getOtherBranches(currentCode: string): BranchData[] {
    return Object.values(branches).filter(branch => branch.code !== currentCode);
}

/**
 * Определить код региона из пути URL
 * @param pathname - путь URL (например, '/geo/spb' или '/')
 * @returns код региона или 'spb' по умолчанию
 */
export function getBranchCodeFromPath(pathname: string): string {
    if (!pathname) return 'spb';
    
    // Если путь начинается с /geo/, извлекаем код региона
    const geoMatch = pathname.match(/^\/(?:[a-z]{2}\/)?geo\/([^\/]+)/i);
    if (geoMatch && geoMatch[1]) {
        const code = geoMatch[1];
        // Проверяем, что такой регион существует
        if (branches[code]) {
            return code;
        }
    }
    
    // По умолчанию возвращаем Санкт-Петербург
    return 'spb';
}

/**
 * Проверить, является ли код региона валидным
 */
export function isValidBranchCode(code: string): boolean {
    return code in branches;
}

/**
 * Удаляет региональный префикс из пути
 * @param path - путь URL (например, '/geo/msk/services' или '/services')
 * @returns путь без регионального префикса (например, '/services')
 */
function removeRegionPrefix(path: string): string {
    if (!path) return '/';
    
    // Удаляем префикс /geo/[code]/ или /geo/[code]
    // Обрабатываем случаи:
    // - /geo/msk/services -> /services
    // - /ru/geo/msk/services -> /ru/services
    // - /geo/msk -> /
    // - /ru/geo/msk -> /ru/
    const geoMatch = path.match(/^(\/[^\/]+)?\/geo\/[^\/]+(\/.*|$)/);
    if (geoMatch) {
        // geoMatch[1] - языковой префикс (если есть), geoMatch[2] - остаток пути
        const langPrefix = geoMatch[1] || '';
        const restPath = geoMatch[2] || '/';
        // Если остаток пути пустой или только слэш, возвращаем корень с языковым префиксом
        if (!restPath || restPath === '/') {
            return langPrefix || '/';
        }
        return langPrefix + restPath;
    }
    
    return path;
}

/**
 * Гарантирует завершающий слэш для внутреннего пути (канон сайта — trailingSlash: true).
 * Без этого внутренние ссылки без слэша получают 308-редирект. Пропускает пути
 * с query/anchor, уже со слэшем и пути-файлы (последний сегмент с расширением).
 */
function ensureTrailingSlash(path: string): string {
    if (!path || path.endsWith('/') || path.includes('?') || path.includes('#')) {
        return path;
    }
    const lastSegment = path.substring(path.lastIndexOf('/') + 1);
    if (lastSegment.includes('.')) {
        return path;
    }
    return `${path}/`;
}

/**
 * Добавляет региональный префикс к пути
 * @param path - путь для региональной локализации (например, '/services' или '/shop/category/koltsa')
 * @param branchCode - код региона (например, 'msk', 'spb')
 * @returns путь с региональным префиксом (например, '/geo/msk/services' для msk, '/services' для spb)
 */
export function regionalizePath(path: string, branchCode: string): string {
    if (!path) return '/';
    
    // Если путь уже является внешней ссылкой (http/https), возвращаем как есть
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//')) {
        return path;
    }
    
    // Если путь начинается с #, возвращаем как есть (якорные ссылки)
    if (path.startsWith('#')) {
        return path;
    }
    
    // Проверяем валидность кода региона
    if (!isValidBranchCode(branchCode)) {
        branchCode = 'spb'; // По умолчанию
    }
    
    // Удаляем существующие региональные префиксы
    const cleanPath = removeRegionPrefix(path);
    
    // Для региона по умолчанию (spb) возвращаем путь без префикса
    if (branchCode === 'spb') {
        return ensureTrailingSlash(cleanPath);
    }
    
    // Для других регионов добавляем префикс /geo/[code]
    // Проверяем, есть ли языковой префикс в начале пути
    const langPrefixMatch = cleanPath.match(/^\/([a-z]{2})(\/|$)/);
    if (langPrefixMatch) {
        // Есть языковой префикс, вставляем региональный после него
        const langPrefix = langPrefixMatch[1];
        const restPath = cleanPath.substring(langPrefix.length + 1) || '/';
        return ensureTrailingSlash(`/${langPrefix}/geo/${branchCode}${restPath === '/' ? '' : restPath}`);
    } else {
        // Нет языкового префикса, добавляем региональный в начало
        return ensureTrailingSlash(`/geo/${branchCode}${cleanPath === '/' ? '' : cleanPath}`);
    }
}
